import {
  ClientAdminItemFragment,
  ConciergeItemFragment,
  GetUserTypeQuery,
  ManagerItemFragment,
  RmcDirectorItemFragment,
  useGetUserTypeQuery,
} from '@/generated/graphql';
import {
  AnalyticsUserType,
  identifyAnalyticsUser,
  isInternalEmail,
} from '@ark/react';
import {createContext, useContext, useEffect, useRef} from 'react';
import {useClarity} from 'use-clarity';
import {
  analyticsClient,
  analyticsStorage,
  isPostHogEnabled,
  resetPostHog,
  startPostHogRecording,
} from '@/posthog';

type CommunityManager = {
  id: string;
};

type UserTypeContextType = {
  email: string;
  manager: ManagerItemFragment | null;
  clientAdmin: ClientAdminItemFragment | null;
  concierge: ConciergeItemFragment | null;
  communityManager: CommunityManager | null;
  rmcDirector: RmcDirectorItemFragment | null;
  loading: boolean;
};

const defaultUserTypeContext: UserTypeContextType = {
  email: '',
  manager: null,
  clientAdmin: null,
  concierge: null,
  communityManager: null,
  rmcDirector: null,
  loading: true,
};

export const useUserTypeContext = () => {
  const context = useContext(UserTypeContext);
  if (!context) {
    throw new Error(
      'useUserTypeContext must be used within a UserTypeProvider',
    );
  }
  return context;
};

const UserTypeContext = createContext<UserTypeContextType>(
  defaultUserTypeContext,
);

const analyticsUserType = (
  user: GetUserTypeQuery['getUser'] | undefined,
): AnalyticsUserType | null => {
  if (user?.clientAdmin) {
    return 'client_admin';
  }
  if (user?.manager) {
    return 'manager';
  }
  if (user?.communityManager) {
    return 'community_manager';
  }
  if (user?.concierge) {
    return 'concierge';
  }
  if (user?.rmcDirector) {
    return 'rmc_director';
  }
  return null;
};

export const UserTypeProvider = ({children}: {children: React.ReactNode}) => {
  const {data, loading} = useGetUserTypeQuery({
    fetchPolicy: 'cache-and-network',
  });
  const {set, identify} = useClarity();
  const identifiedUserId = useRef<string | null>(null);

  const userId = data?.getUser?.id;
  const email = data?.getUser?.email ?? '';
  const name = data?.getUser?.name ?? null;
  const clientAdmin = data?.getUser?.clientAdmin ?? null;
  const manager = data?.getUser?.manager ?? null;
  const concierge = data?.getUser?.concierge ?? null;
  const communityManager = data?.getUser?.communityManager ?? null;
  const rmcDirector = data?.getUser?.rmcDirector ?? null;
  const userType = analyticsUserType(data?.getUser);
  const userClient =
    clientAdmin?.client ?? manager?.client ?? concierge?.client ?? null;
  const analyticsClientId = userClient?.id;
  const analyticsClientName = userClient?.name;
  const analyticsClientType = userClient?.clientType;

  useEffect(() => {
    if (!isPostHogEnabled || !userId) {
      return;
    }
    if (identifiedUserId.current && identifiedUserId.current !== userId) {
      resetPostHog();
    }
    void identifyAnalyticsUser(analyticsClient, analyticsStorage, {
      distinctId: userId,
      person: {
        email: email || null,
        name,
        user_type: userType,
        is_internal: isInternalEmail(email),
        is_demo: null,
      },
      groups: {
        client:
          analyticsClientId && analyticsClientName
            ? {
                key: analyticsClientId,
                name: analyticsClientName,
                clientType: analyticsClientType,
              }
            : undefined,
      },
    });
    startPostHogRecording();
    identifiedUserId.current = userId;
  }, [
    analyticsClientId,
    analyticsClientName,
    analyticsClientType,
    email,
    name,
    userId,
    userType,
  ]);

  useEffect(() => {
    const conciergeClient = data?.getUser?.concierge?.client;
    const managerClient = data?.getUser?.manager?.client;
    const clientAdminClient = data?.getUser?.clientAdmin?.client;

    try {
      if (clientAdminClient) {
        set('client', clientAdminClient.name);
      } else if (managerClient) {
        set('client', managerClient.name);
      } else if (conciergeClient) {
        set('client', conciergeClient.name);
      }
      if (data?.getUser?.email) {
        identify(data?.getUser?.email);
      }
    } catch (e) {
      //do nothing
    }
  }, [data?.getUser]);

  return (
    <UserTypeContext.Provider
      value={{
        email,
        manager,
        clientAdmin,
        concierge,
        communityManager,
        rmcDirector,
        loading: loading && !data?.getUser,
      }}
    >
      {children}
    </UserTypeContext.Provider>
  );
};
